import { useState } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/layout/PageHeader';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

export default function BulkImportPage() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.match(/\.(csv|xlsx|xls)$/i)) {
        setFile(droppedFile);
        setResult(null);
      } else {
        toast.error('Please upload a CSV or Excel file');
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/evidence/bulk-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setResult(response.data);
      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} evidence items`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import file');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get('/evidence/import-template', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'evidence_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Bulk Evidence Import"
        subtitle="Import multiple evidence items from CSV or Excel files"
        icon={<FileSpreadsheet size={28} />}
        actions={
          <Button
            data-testid="download-template-btn"
            variant="outline"
            onClick={downloadTemplate}
            className="rounded-sm"
          >
            <Download size={18} className="mr-2" />
            Download Template
          </Button>
        }
      />

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-sm p-6 mb-8">
        <h3 className="font-semibold text-blue-900 mb-2">How to Import Evidence</h3>
        <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
          <li>Download the CSV template using the button above</li>
          <li>Fill in your evidence data (title is required, other fields are optional)</li>
          <li>Use control codes in the <code className="bg-blue-100 px-1 rounded">control_codes</code> column to link evidence to controls</li>
          <li>Upload the file using the form below</li>
        </ol>
        <div className="mt-4 text-sm text-blue-700">
          <strong>Supported formats:</strong> CSV (.csv), Excel (.xlsx, .xls)
        </div>
      </div>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-sm p-12 text-center transition-colors ${
          dragActive 
            ? 'border-blue-500 bg-blue-50' 
            : file 
            ? 'border-emerald-500 bg-emerald-50' 
            : 'border-slate-300 bg-slate-50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="file-upload"
          data-testid="file-upload-input"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
          className="hidden"
        />
        
        {file ? (
          <div>
            <FileSpreadsheet size={48} className="mx-auto text-emerald-500 mb-4" />
            <p className="font-medium text-lg mb-1">{file.name}</p>
            <p className="text-sm text-slate-500 mb-4">
              {(file.size / 1024).toFixed(1)} KB
            </p>
            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setFile(null);
                  setResult(null);
                }}
                className="rounded-sm"
              >
                Remove
              </Button>
              <Button
                data-testid="upload-btn"
                onClick={handleUpload}
                disabled={uploading}
                className="bg-emerald-600 hover:bg-emerald-700 rounded-sm"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload size={18} className="mr-2" />
                    Import Evidence
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <label htmlFor="file-upload" className="cursor-pointer">
            <Upload size={48} className="mx-auto text-slate-400 mb-4" />
            <p className="font-medium text-lg mb-1">Drop your file here or click to browse</p>
            <p className="text-sm text-slate-500">
              Supports CSV and Excel files
            </p>
          </label>
        )}
      </div>

      {/* Results */}
      {result && (
        <div className="mt-8 bg-white border border-slate-200 rounded-sm p-6">
          <h3 className="font-semibold text-lg mb-4">Import Results</h3>
          
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-slate-50 rounded-sm p-4 text-center">
              <div className="text-2xl font-bold text-slate-700">{result.total_rows}</div>
              <div className="text-sm text-slate-500">Total Rows</div>
            </div>
            <div className="bg-emerald-50 rounded-sm p-4 text-center">
              <div className="text-2xl font-bold text-emerald-600 flex items-center justify-center gap-2">
                <CheckCircle size={24} />
                {result.imported}
              </div>
              <div className="text-sm text-emerald-600">Imported</div>
            </div>
            <div className={`rounded-sm p-4 text-center ${result.failed > 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
              <div className={`text-2xl font-bold flex items-center justify-center gap-2 ${result.failed > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                {result.failed > 0 ? <XCircle size={24} /> : null}
                {result.failed}
              </div>
              <div className={`text-sm ${result.failed > 0 ? 'text-red-600' : 'text-slate-500'}`}>Failed</div>
            </div>
          </div>

          {result.errors && result.errors.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={18} className="text-amber-600" />
                <span className="font-medium text-amber-800">Import Errors</span>
              </div>
              <ul className="text-sm text-amber-700 space-y-1">
                {result.errors.map((err, idx) => (
                  <li key={idx} className="font-mono">{err}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Expected Columns */}
      <div className="mt-8 bg-white border border-slate-200 rounded-sm p-6">
        <h3 className="font-semibold text-lg mb-4">Expected Columns</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50">
                <th className="px-4 py-2 text-left font-medium">Column</th>
                <th className="px-4 py-2 text-left font-medium">Required</th>
                <th className="px-4 py-2 text-left font-medium">Description</th>
                <th className="px-4 py-2 text-left font-medium">Example</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              <tr>
                <td className="px-4 py-2 font-mono">title</td>
                <td className="px-4 py-2"><span className="text-red-600">Yes</span></td>
                <td className="px-4 py-2">Evidence title</td>
                <td className="px-4 py-2 text-slate-500">AWS S3 Bucket Encryption Config</td>
              </tr>
              <tr>
                <td className="px-4 py-2 font-mono">description</td>
                <td className="px-4 py-2">No</td>
                <td className="px-4 py-2">Detailed description</td>
                <td className="px-4 py-2 text-slate-500">Evidence of S3 encryption settings</td>
              </tr>
              <tr>
                <td className="px-4 py-2 font-mono">source_type</td>
                <td className="px-4 py-2">No</td>
                <td className="px-4 py-2">Source of evidence</td>
                <td className="px-4 py-2 text-slate-500">automated, manual</td>
              </tr>
              <tr>
                <td className="px-4 py-2 font-mono">status</td>
                <td className="px-4 py-2">No</td>
                <td className="px-4 py-2">Compliance status</td>
                <td className="px-4 py-2 text-slate-500">pending, compliant, non_compliant</td>
              </tr>
              <tr>
                <td className="px-4 py-2 font-mono">control_codes</td>
                <td className="px-4 py-2">No</td>
                <td className="px-4 py-2">Comma-separated control codes</td>
                <td className="px-4 py-2 text-slate-500">CC6.1,CC7.2,A.9.1.1</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
